import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  alpha,
  Chip,
  LinearProgress,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Description as DocumentIcon,
  CheckCircle as ApprovedIcon,
  Warning as PendingIcon,
  Error as ExceptionIcon,
  Hub as HubIcon,
  Cloud as CloudIcon,
  SyncAlt as SyncIcon,
  RadioButtonChecked as ActiveDotIcon,
} from '@mui/icons-material';
import DocumentUpload from './DocumentUpload';
import DocumentReview from './DocumentReview';
import ExceptionQueue from './ExceptionQueue';
import UnloadingReceiptUpload from './Process2/UnloadingReceiptUpload';
import ReceiptQueue from './Process2/ReceiptQueue';
import PalletVoucherUpload from './Process3/PalletVoucherUpload';
import VehicleUpload from './Process4/VehicleUpload';
import { useLanguage } from '../i18n';
import { fetchDashboardStats, DashboardStats } from '../services/api';
import DashboardCharts from './DashboardCharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`process-tabpanel-${index}`}
      aria-labelledby={`process-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, changeType, icon, color, loading }) => (
  <Card
    sx={{
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: color,
      },
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={80} height={48} />
          ) : (
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
          )}
          {change && (
            <Chip
              size="small"
              label={change}
              sx={{
                backgroundColor: alpha(
                  changeType === 'positive' ? '#10B981' : changeType === 'negative' ? '#EF4444' : '#64748B',
                  0.1
                ),
                color: changeType === 'positive' ? '#059669' : changeType === 'negative' ? '#DC2626' : '#64748B',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(color, 0.1),
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/* ─── Connector Panel ─────────────────────────────────── */
const CONNECTOR_DATA = [
  { name: 'Salesforce CRM',  color: '#00A1E0', status: 'active',      synced: '2 min ago',  records: '142' },
  { name: 'SAP / ERP',       color: '#F0AB00', status: 'active',      synced: '5 min ago',  records: '874' },
  { name: 'AWS S3',          color: '#FF9900', status: 'active',      synced: 'Just now',   records: '390' },
  { name: 'Azure Blob',      color: '#0089D6', status: 'in_progress', synced: 'Syncing...', records: '120' },
  { name: 'GCP Storage',     color: '#4285F4', status: 'active',      synced: '12 min ago', records: '221' },
  { name: 'Oracle DB',       color: '#F80000', status: 'in_progress', synced: 'Syncing...', records: '905' },
  { name: 'SharePoint',      color: '#0078D4', status: 'idle',        synced: '1 hr ago',   records: '567' },
  { name: 'Snowflake',       color: '#29B5E8', status: 'active',      synced: '8 min ago',  records: '413' },
];

const statusMeta: Record<string, { label: string; bg: string; fg: string }> = {
  active:      { label: 'Active',      bg: 'rgba(16,185,129,0.12)', fg: '#10B981' },
  in_progress: { label: 'In Progress', bg: 'rgba(245,158,11,0.12)', fg: '#F59E0B' },
  idle:        { label: 'Idle',        bg: 'rgba(100,116,139,0.12)', fg: '#94A3B8' },
};

const ConnectorPanel: React.FC = () => {
  const active      = CONNECTOR_DATA.filter(c => c.status === 'active').length;
  const inProgress  = CONNECTOR_DATA.filter(c => c.status === 'in_progress').length;
  const idle        = CONNECTOR_DATA.filter(c => c.status === 'idle').length;

  return (
    <Card sx={{ mb: 4, overflow: 'hidden', position: 'relative' }}>
      {/* top accent bar */}
      <Box sx={{ height: 4, background: 'linear-gradient(90deg,#0EA5E9 0%,#2563EB 50%,#8B5CF6 100%)' }} />
      <CardContent sx={{ p: 3 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 2,
              background: 'linear-gradient(135deg,#0EA5E9,#2563EB)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <HubIcon sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Data Source Connectors</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{CONNECTOR_DATA.length} connectors configured</Typography>
            </Box>
          </Box>
          {/* Summary chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" icon={<ActiveDotIcon sx={{ fontSize: '14px !important', color: '#10B981 !important' }} />}
              label={`${active} Active`}
              sx={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', fontWeight: 700, fontSize: '0.72rem' }} />
            <Chip size="small" icon={<SyncIcon sx={{ fontSize: '14px !important', color: '#F59E0B !important' }} />}
              label={`${inProgress} In Progress`}
              sx={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', fontWeight: 700, fontSize: '0.72rem' }} />
            <Chip size="small"
              label={`${idle} Idle`}
              sx={{ background: 'rgba(100,116,139,0.1)', color: '#94A3B8', fontWeight: 700, fontSize: '0.72rem' }} />
          </Box>
        </Box>

        {/* Connector grid */}
        <Grid container spacing={1.5}>
          {CONNECTOR_DATA.map((c) => {
            const meta = statusMeta[c.status];
            return (
              <Grid item xs={12} sm={6} md={3} key={c.name}>
                <Box sx={{
                  p: 2, borderRadius: 2,
                  border: `1px solid ${c.color}28`,
                  background: `${c.color}08`,
                  display: 'flex', flexDirection: 'column', gap: 1,
                  transition: 'all 0.2s',
                  '&:hover': { background: `${c.color}14`, transform: 'translateY(-2px)', boxShadow: `0 6px 20px ${c.color}20` },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: c.color, flexShrink: 0,
                        boxShadow: c.status === 'active' ? `0 0 6px ${c.color}` : 'none',
                        animation: c.status === 'active' ? 'pulse 2s ease-in-out infinite' : 'none',
                      }} />
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.primary' }}>{c.name}</Typography>
                    </Box>
                    <Chip size="small" label={meta.label}
                      sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, px: 0.25,
                        background: meta.bg, color: meta.fg,
                        '& .MuiChip-label': { px: 0.75 },
                      }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                      <CloudIcon sx={{ fontSize: 10, mr: 0.4, verticalAlign: 'middle', opacity: 0.6 }} />
                      {c.synced}
                    </Typography>
                    <Typography variant="caption" sx={{ color: c.color, fontWeight: 700, fontSize: '0.68rem' }}>
                      {c.records} records
                    </Typography>
                  </Box>
                  {/* thin progress bar */}
                  <Box sx={{ height: 3, borderRadius: 2, background: `${c.color}20`, overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', borderRadius: 2, background: c.color,
                      width: c.status === 'active' ? '100%' : c.status === 'in_progress' ? '55%' : '30%',
                      transition: 'width 1s ease',
                    }} />
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

function Dashboard() {
  const [value, setValue] = useState(0);
  const { t } = useLanguage();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const data = await fetchDashboardStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t.dashboard.title}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t.dashboard.subtitle}
        </Typography>
      </Box>
      {/* Full-page loading spinner */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
          <CircularProgress size={56} thickness={4} sx={{ color: '#6366F1' }} />
        </Box>
      )}

      {!loading && (
      <>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={2.4}>
          <StatCard
            title={t.dashboard.totalDocuments}
            value={stats?.total_documents ?? 0}
            loading={loading}
            icon={<DocumentIcon sx={{ fontSize: 28 }} />}
            color="#3B82F6"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <StatCard
            title={t.dashboard.approved}
            value={stats?.approved ?? 0}
            loading={loading}
            icon={<ApprovedIcon sx={{ fontSize: 28 }} />}
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <StatCard
            title="Processed"
            value={stats?.processed ?? 0}
            loading={loading}
            icon={<DocumentIcon sx={{ fontSize: 28 }} />}
            color="#6366F1"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <StatCard
            title={t.dashboard.pendingReview}
            value={stats?.pending_review ?? 0}
            loading={loading}
            icon={<PendingIcon sx={{ fontSize: 28 }} />}
            color="#F59E0B"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <StatCard
            title="Rejected"
            value={stats?.rejected ?? 0}
            loading={loading}
            icon={<ExceptionIcon sx={{ fontSize: 28 }} />}
            color="#EF4444"
          />
        </Grid>
      </Grid>

      {/* Processing Progress */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t.dashboard.processingProgress}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t.dashboard.todayStatus}
              </Typography>
            </Box>
            {stats && (
              <Chip
                icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                label={`${stats.total_documents} docs`}
                color="success"
                size="small"
              />
            )}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t.dashboard.dailyFeePayment} ({stats?.process_counts?.daily_fee ?? 0})
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {loading ? <CircularProgress size={14} thickness={5} sx={{ color: 'text.secondary' }} /> : `${stats?.progress?.daily_fee ?? 0}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats?.progress?.daily_fee ?? 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha('#3B82F6', 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)',
                },
              }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t.dashboard.unloadingPayment} ({stats?.process_counts?.unloading ?? 0})
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {loading ? <CircularProgress size={14} thickness={5} sx={{ color: 'text.secondary' }} /> : `${stats?.progress?.unloading ?? 0}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats?.progress?.unloading ?? 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha('#10B981', 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                },
              }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t.dashboard.palletReconciliation} ({stats?.process_counts?.pallet ?? 0})
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {loading ? <CircularProgress size={14} thickness={5} sx={{ color: 'text.secondary' }} /> : `${stats?.progress?.pallet ?? 0}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats?.progress?.pallet ?? 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha('#F59E0B', 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)',
                },
              }}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t.dashboard.vehicleRegistration} ({stats?.process_counts?.vehicle ?? 0})
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {loading ? <CircularProgress size={14} thickness={5} sx={{ color: 'text.secondary' }} /> : `${stats?.progress?.vehicle ?? 0}%`}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stats?.progress?.vehicle ?? 0}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha('#8B5CF6', 0.1),
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: 'linear-gradient(90deg, #8B5CF6 0%, #7C3AED 100%)',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Connectors Panel */}
      <ConnectorPanel />

      {/* Analytics Charts */}
      {stats && (
        <Box sx={{ mb: 4 }}>
          <DashboardCharts stats={stats} />
        </Box>
      )}

      {/* Process Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="process tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 56,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9375rem',
              },
            }}
          >
            <Tab label={t.dashboard.dailyFeePayment} />
            <Tab label={t.dashboard.unloadingPayment} />
            <Tab label={t.dashboard.palletReconciliation} />
            <Tab label={t.dashboard.vehicleRegistration} />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          <TabPanel value={value} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {t.dashboard.uploadDocument}
                    </Typography>
                    <DocumentUpload />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {t.nav.documentReview}
                    </Typography>
                    <DocumentReview />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {t.nav.exceptionQueue}
                    </Typography>
                    <ExceptionQueue />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={value} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <UnloadingReceiptUpload />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <ReceiptQueue />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={value} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <PalletVoucherUpload />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={value} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <VehicleUpload />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </Card>
      </>
      )}
    </Box>
  );
}

export default Dashboard;

